import { __decorate } from 'tslib';
import * as i0 from '@angular/core';
import { Component, ChangeDetectionStrategy, Input, HostBinding, EventEmitter, inject, ChangeDetectorRef, ContentChildren, ViewChildren, ViewChild, Output, NgModule } from '@angular/core';
import { isRTL, WebComponentContentChildren } from '@simpl/element-ng/common';
import { asyncScheduler } from 'rxjs';
import { first, observeOn } from 'rxjs/operators';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i2 from '@simpl/element-ng/icon';
import { SiIconModule } from '@simpl/element-ng/icon';
import * as i3 from '@simpl/element-ng/resize-observer';
import { SiResizeObserverModule } from '@simpl/element-ng/resize-observer';
import * as i4 from '@simpl/element-ng/translate';
import { SiTranslateModule } from '@simpl/element-ng/translate';

class SiTabComponent {
    static idCounter = 0;
    /** Title of the tab item. */
    heading;
    /** Icon of the tab item. */
    icon;
    /** Alternative name or translation key for icon. Used for A11y. */
    iconAltText;
    /** Additional status badge content. */
    badgeContent;
    /** Background color of the additional status badge. */
    badgeColor;
    /** Disables the tab. */
    disabled = false;
    _hidden = false;
    get hidden() {
        return this._hidden;
    }
    /** Hides the tab item completely if set.
     *
     * @deprecated Use *ngIf instead
     */
    set hidden(val) {
        // WebComponent has a conflict when used same property name as Input and HTML native attribute binding.
        // We ignore binding if it is coming as attribute string.
        if (typeof val === 'string') {
            return;
        }
        this._hidden = val;
    }
    id = `__si-tab-panel-${SiTabComponent.idCounter++}`;
    tabId = `__si-tab-${SiTabComponent.idCounter}`;
    get isHidden() {
        return this.hidden || !this.active ? true : null;
    }
    parent;
    /** @internal */
    active = false;
    /** @internal */
    registerParent(parent) {
        this.parent = parent;
    }
    ngOnChanges() {
        this.parent?.notifyChildrenChanged();
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiTabComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.1.5", type: SiTabComponent, selector: "si-tab", inputs: { heading: "heading", icon: "icon", iconAltText: "iconAltText", badgeContent: "badgeContent", badgeColor: "badgeColor", disabled: "disabled", hidden: "hidden" }, host: { attributes: { "role": "tabpanel" }, properties: { "id": "this.id", "attr.aria-labelledby": "this.tabId", "attr.hidden": "this.isHidden" } }, usesOnChanges: true, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiTabComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'si-tab',
                    template: '<ng-content></ng-content>',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    host: {
                        role: 'tabpanel'
                    }
                }]
        }], propDecorators: { heading: [{
                type: Input
            }], icon: [{
                type: Input
            }], iconAltText: [{
                type: Input
            }], badgeContent: [{
                type: Input
            }], badgeColor: [{
                type: Input
            }], disabled: [{
                type: Input
            }], hidden: [{
                type: Input
            }], id: [{
                type: HostBinding,
                args: ['id']
            }], tabId: [{
                type: HostBinding,
                args: ['attr.aria-labelledby']
            }], isHidden: [{
                type: HostBinding,
                args: ['attr.hidden']
            }] } });

const SCROLL_INCREMENT = 55;
class SiTabsetComponent {
    initialized = false;
    /**
     * Contains the current tab components.
     */
    tabPanels;
    tabs;
    /**
     * Component variable to indicate if scrolling is necessary or the container is big enough to display all tabs.
     */
    scrollable = false;
    xPos = 0;
    endArrowDisabled = false;
    tabContainer;
    innerTabContainer;
    /**
     * If selectDefaultTab is passed as 'false', this implies no default tab selection
     * i.e. on initial load of tabset component no tab gets selected.
     */
    selectDefaultTab = true;
    /**
     * Sets a selected tab index. This will activate the tab of the provided
     * index, activates the tab and fires a notification about the change.
     * If index is passed as -1 i.e. `selectedTabIndex = -1`, this implies to clear all tab selection.
     * @defaultref {@link _selectedTabIndex}
     */
    set selectedTabIndex(tabIndex) {
        if (this.initialized && this.tabPanels.get(tabIndex)) {
            this.selectTab(this.tabPanels.get(tabIndex));
        }
        else {
            this.initTabIndex = tabIndex;
        }
    }
    /**
     * Returns the currently selected tab index.
     */
    get selectedTabIndex() {
        return this.tabPanels?.toArray().findIndex(tab => tab.active);
    }
    /**
     * Event emitter to notify about selected tab index changes. You can either
     * use bi-directional binding with [(selectedTabIndex)] or separate both with
     * [selectedTabIndex]=... and (selectedTabIndexChange)=...
     */
    selectedTabIndexChange = new EventEmitter();
    /**
     * Event emitter to notify when a tab became inactive.
     */
    deselect = new EventEmitter();
    focusedTabIndex;
    initTabIndex = 0;
    subscription;
    changeDetectorRef = inject(ChangeDetectorRef);
    ngAfterViewInit() {
        this.initialized = true;
        this.subscription = this.tabPanels.changes.subscribe(() => {
            this.tabPanels.forEach(tab => tab.registerParent(this));
            this.changeDetectorRef.markForCheck();
        });
        this.tabPanels.notifyOnChanges();
        if (this.selectDefaultTab) {
            if (this.tabPanels.length) {
                queueMicrotask(() => this.selectTab(this.tabPanels.get(this.initTabIndex)));
            }
            else {
                // no tabs are there yet. But maybe there will be some soon, so let's wait for it
                this.subscription.add(this.tabPanels.changes
                    .pipe(first(() => !!this.tabPanels.length), observeOn(asyncScheduler))
                    .subscribe(() => {
                    this.selectTab(this.tabPanels.get(this.initTabIndex));
                    this.changeDetectorRef.markForCheck();
                }));
            }
        }
        setTimeout(() => {
            this.resize();
        });
    }
    ngOnDestroy() {
        this.subscription?.unsubscribe();
        this.subscription = undefined;
    }
    /** @internal */
    notifyChildrenChanged() {
        this.changeDetectorRef.markForCheck();
    }
    focusNext() {
        do {
            this.focusedTabIndex++;
            if (this.focusedTabIndex >= this.tabs.length) {
                this.focusedTabIndex = 0;
            }
        } while (this.tabPanels.get(this.focusedTabIndex).hidden ||
            this.tabPanels.get(this.focusedTabIndex).disabled);
        this.tabs.get(this.focusedTabIndex).nativeElement.focus();
    }
    focusPrevious() {
        do {
            this.focusedTabIndex--;
            if (this.focusedTabIndex < 0) {
                this.focusedTabIndex += this.tabs.length;
            }
        } while (this.tabPanels.get(this.focusedTabIndex).hidden ||
            this.tabPanels.get(this.focusedTabIndex).disabled);
        this.tabs.get(this.focusedTabIndex).nativeElement.focus();
    }
    trackByIndex = (index) => index;
    resize() {
        this.scrollable =
            Math.round(this.tabContainer.nativeElement.offsetWidth) <
                this.innerTabContainer.nativeElement.scrollWidth;
        this.scroll(0);
    }
    /**
     * Finds the index of the provided tab and sets the index as new selected tab index
     *
     * @param selectedTab The tab to be selected. This must already be part of the container.
     */
    selectTab(selectedTab) {
        if (selectedTab?.disabled) {
            return;
        }
        const tabs = this.tabPanels.toArray();
        const newTabIndex = tabs.indexOf(selectedTab);
        const currentTabIndex = tabs.findIndex(tab => tab.active);
        let continueWithSelection = newTabIndex !== currentTabIndex;
        if (continueWithSelection && currentTabIndex !== -1) {
            const currentTab = tabs[currentTabIndex];
            const deselectEvent = {
                target: currentTab,
                tabIndex: currentTabIndex,
                cancel: () => {
                    continueWithSelection = false;
                    currentTab.active = true;
                }
            };
            currentTab.active = false;
            this.deselect.emit(deselectEvent);
        }
        if (continueWithSelection) {
            selectedTab.active = true;
            this.selectedTabIndexChange.emit(newTabIndex);
        }
    }
    /**
     * Scrolls the tab headers to the end (right in LTR).
     */
    scrollEnd() {
        this.scroll(SCROLL_INCREMENT);
    }
    /**
     * Scrolls the tab headers to the start (left in LTR).
     */
    scrollStart() {
        this.scroll(-SCROLL_INCREMENT);
    }
    scroll(inc) {
        this.xPos += inc;
        this.xPos = Math.max(0, Math.min(this.innerTabContainer.nativeElement.scrollWidth -
            this.innerTabContainer.nativeElement.offsetWidth, this.xPos));
        this.endArrowDisabled =
            this.xPos + this.tabContainer.nativeElement.offsetWidth >=
                this.innerTabContainer.nativeElement.scrollWidth;
        this.innerTabContainer.nativeElement.style.transform = `translateX(${this.xPos * (isRTL() ? 1 : -1)}px)`;
    }
    mouseScroll(event) {
        if (event.deltaY < 0) {
            this.scroll(-SCROLL_INCREMENT);
        }
        else {
            this.scroll(SCROLL_INCREMENT);
        }
        if (this.xPos || !this.endArrowDisabled) {
            event.preventDefault();
        }
    }
    /** @internal */
    focus(index) {
        this.focusedTabIndex = index;
    }
    /** @internal */
    blur() {
        this.focusedTabIndex = undefined;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiTabsetComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.1.5", type: SiTabsetComponent, selector: "si-tabset", inputs: { selectDefaultTab: "selectDefaultTab", selectedTabIndex: "selectedTabIndex" }, outputs: { selectedTabIndexChange: "selectedTabIndexChange", deselect: "deselect" }, queries: [{ propertyName: "tabPanels", predicate: SiTabComponent }], viewQueries: [{ propertyName: "tabContainer", first: true, predicate: ["tabContainer"], descendants: true, static: true }, { propertyName: "innerTabContainer", first: true, predicate: ["innerTabContainer"], descendants: true, static: true }, { propertyName: "tabs", predicate: ["tabElement"], descendants: true }], ngImport: i0, template: "<div\n  class=\"nav nav-tabs tab-container-header focus-inside\"\n  role=\"tablist\"\n  [class.is-scrollable]=\"scrollable\"\n  (siResizeObserver)=\"resize()\"\n>\n  <button\n    *ngIf=\"scrollable\"\n    type=\"button\"\n    class=\"tab-container-control focus-inside\"\n    tabindex=\"-1\"\n    aria-hidden=\"true\"\n    [hidden]=\"!xPos\"\n    (keydown.enter)=\"scrollStart()\"\n    (mousedown)=\"scrollStart()\"\n  >\n    <i class=\"icon element-left-3 flip-rtl\"></i>\n  </button>\n\n  <div #tabContainer class=\"tab-container-buttonbar\" (wheel)=\"mouseScroll($event)\">\n    <div #innerTabContainer class=\"tab-container-buttonbar-list focus-inside\">\n      <ng-container *ngFor=\"let tab of tabPanels; let index = index; trackBy: trackByIndex\">\n        <button\n          #tabElement\n          type=\"button\"\n          role=\"tab\"\n          class=\"nav-link focus-inside px-5\"\n          [attr.hidden]=\"tab.hidden || null\"\n          [attr.aria-controls]=\"tab.id\"\n          [id]=\"tab.tabId\"\n          [tabindex]=\"!tab.disabled && tab.active && focusedTabIndex === undefined ? 0 : -1\"\n          [class.active]=\"tab.active\"\n          [attr.aria-selected]=\"tab.active\"\n          [class.disabled]=\"tab.disabled\"\n          (click)=\"selectTab(tab)\"\n          (keydown.arrowLeft)=\"focusPrevious()\"\n          (keydown.arrowRight)=\"focusNext()\"\n          (blur)=\"blur()\"\n          (focus)=\"focus(index)\"\n        >\n          <span class=\"text-nowrap\">{{ tab.heading! | translate }}</span>\n          <si-icon *ngIf=\"tab.icon\" [icon]=\"tab.icon\" [alt]=\"tab.iconAltText\"></si-icon>\n          <span *ngIf=\"tab.badgeContent\" class=\"badge\" [ngClass]=\"'bg-' + tab.badgeColor\">\n            {{ tab.badgeContent | translate }}\n          </span>\n        </button>\n      </ng-container>\n    </div>\n  </div>\n  <button\n    *ngIf=\"scrollable\"\n    type=\"button\"\n    class=\"tab-container-control is-end focus-inside\"\n    tabindex=\"-1\"\n    aria-hidden=\"true\"\n    [hidden]=\"endArrowDisabled\"\n    (keydown.enter)=\"scrollEnd()\"\n    (mousedown)=\"scrollEnd()\"\n  >\n    <i class=\"icon element-right-3 flip-rtl\"></i>\n  </button>\n</div>\n\n<ng-content></ng-content>\n", styles: [":host{display:block;flex-direction:column}*:focus{outline:0}.tab-container-header{overflow:hidden;display:flex}.tab-container-buttonbar{flex:1;overflow:hidden}.tab-container-buttonbar-list{display:flex;margin:0;padding:0;list-style-type:none;transition:calc(.25s * var(--element-animations-enabled, 1))}.tab-container-buttonbar-list-item{flex:none}.tab-container-control{inline-size:40px;block-size:38px;background:transparent;border:0;-webkit-user-select:none;user-select:none}.tab-container-control i{font-size:30px}.tab-container-control:hover{color:var(--element-text-active)}.tab-container-control:disabled{color:var(--element-text-disabled);cursor:default}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i2.SiIconComponent, selector: "si-icon", inputs: ["icon", "color", "stackedIcon", "stackedColor", "alt", "size"] }, { kind: "directive", type: i3.SiResizeObserverDirective, selector: "[siResizeObserver]", inputs: ["resizeThrottle"], outputs: ["siResizeObserver"] }, { kind: "pipe", type: i4.SiTranslatePipe, name: "translate" }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
__decorate([
    WebComponentContentChildren(SiTabComponent)
], SiTabsetComponent.prototype, "tabPanels", void 0);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiTabsetComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-tabset', changeDetection: ChangeDetectionStrategy.OnPush, template: "<div\n  class=\"nav nav-tabs tab-container-header focus-inside\"\n  role=\"tablist\"\n  [class.is-scrollable]=\"scrollable\"\n  (siResizeObserver)=\"resize()\"\n>\n  <button\n    *ngIf=\"scrollable\"\n    type=\"button\"\n    class=\"tab-container-control focus-inside\"\n    tabindex=\"-1\"\n    aria-hidden=\"true\"\n    [hidden]=\"!xPos\"\n    (keydown.enter)=\"scrollStart()\"\n    (mousedown)=\"scrollStart()\"\n  >\n    <i class=\"icon element-left-3 flip-rtl\"></i>\n  </button>\n\n  <div #tabContainer class=\"tab-container-buttonbar\" (wheel)=\"mouseScroll($event)\">\n    <div #innerTabContainer class=\"tab-container-buttonbar-list focus-inside\">\n      <ng-container *ngFor=\"let tab of tabPanels; let index = index; trackBy: trackByIndex\">\n        <button\n          #tabElement\n          type=\"button\"\n          role=\"tab\"\n          class=\"nav-link focus-inside px-5\"\n          [attr.hidden]=\"tab.hidden || null\"\n          [attr.aria-controls]=\"tab.id\"\n          [id]=\"tab.tabId\"\n          [tabindex]=\"!tab.disabled && tab.active && focusedTabIndex === undefined ? 0 : -1\"\n          [class.active]=\"tab.active\"\n          [attr.aria-selected]=\"tab.active\"\n          [class.disabled]=\"tab.disabled\"\n          (click)=\"selectTab(tab)\"\n          (keydown.arrowLeft)=\"focusPrevious()\"\n          (keydown.arrowRight)=\"focusNext()\"\n          (blur)=\"blur()\"\n          (focus)=\"focus(index)\"\n        >\n          <span class=\"text-nowrap\">{{ tab.heading! | translate }}</span>\n          <si-icon *ngIf=\"tab.icon\" [icon]=\"tab.icon\" [alt]=\"tab.iconAltText\"></si-icon>\n          <span *ngIf=\"tab.badgeContent\" class=\"badge\" [ngClass]=\"'bg-' + tab.badgeColor\">\n            {{ tab.badgeContent | translate }}\n          </span>\n        </button>\n      </ng-container>\n    </div>\n  </div>\n  <button\n    *ngIf=\"scrollable\"\n    type=\"button\"\n    class=\"tab-container-control is-end focus-inside\"\n    tabindex=\"-1\"\n    aria-hidden=\"true\"\n    [hidden]=\"endArrowDisabled\"\n    (keydown.enter)=\"scrollEnd()\"\n    (mousedown)=\"scrollEnd()\"\n  >\n    <i class=\"icon element-right-3 flip-rtl\"></i>\n  </button>\n</div>\n\n<ng-content></ng-content>\n", styles: [":host{display:block;flex-direction:column}*:focus{outline:0}.tab-container-header{overflow:hidden;display:flex}.tab-container-buttonbar{flex:1;overflow:hidden}.tab-container-buttonbar-list{display:flex;margin:0;padding:0;list-style-type:none;transition:calc(.25s * var(--element-animations-enabled, 1))}.tab-container-buttonbar-list-item{flex:none}.tab-container-control{inline-size:40px;block-size:38px;background:transparent;border:0;-webkit-user-select:none;user-select:none}.tab-container-control i{font-size:30px}.tab-container-control:hover{color:var(--element-text-active)}.tab-container-control:disabled{color:var(--element-text-disabled);cursor:default}\n"] }]
        }], propDecorators: { tabPanels: [{
                type: ContentChildren,
                args: [SiTabComponent]
            }], tabs: [{
                type: ViewChildren,
                args: ['tabElement']
            }], tabContainer: [{
                type: ViewChild,
                args: ['tabContainer', { static: true }]
            }], innerTabContainer: [{
                type: ViewChild,
                args: ['innerTabContainer', { static: true }]
            }], selectDefaultTab: [{
                type: Input
            }], selectedTabIndex: [{
                type: Input
            }], selectedTabIndexChange: [{
                type: Output
            }], deselect: [{
                type: Output
            }] } });

const components = [SiTabsetComponent, SiTabComponent];
class SiTabsModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiTabsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.1.5", ngImport: i0, type: SiTabsModule, declarations: [SiTabsetComponent, SiTabComponent], imports: [CommonModule, SiIconModule, SiResizeObserverModule, SiTranslateModule], exports: [SiTabsetComponent, SiTabComponent] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiTabsModule, imports: [CommonModule, SiIconModule, SiResizeObserverModule, SiTranslateModule] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiTabsModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, SiIconModule, SiResizeObserverModule, SiTranslateModule],
                    declarations: components,
                    exports: components
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { SiTabComponent, SiTabsModule, SiTabsetComponent };
